import { Card } from '@pilot-hub/ui'
import { phasenFuerTyp, phaseIndex, type EinsatzPhaseKey } from '@pilot-hub/ui'
import { Check, Clock, Loader2 } from 'lucide-react'

const ZEIT_FELD: Partial<Record<EinsatzPhaseKey, string>> = {
  ALARMIERUNG: 'alarmierungszeit', ABFAHRT_WACHE: 'abfahrt_zeit', ANKUNFT_EINSATZORT: 'ankunft_zeit',
  EINSATZENDE: 'einsatzende_zeit', EINSATZBEREIT: 'einsatzbereit_zeit',
}

const SPALTEN: Record<number, string> = { 5: 'grid-cols-5', 6: 'grid-cols-3 sm:grid-cols-6', 7: 'grid-cols-4 sm:grid-cols-7' }

const uhrzeit = (s?: string | null) => (s ? new Date(s).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '')

/** Zeitleiste nur mit den Phasen; nur die Führungskraft setzt sie (Klick auf eine Phase). */
export function PhasenZeitleiste({ einsatz, aktuell, aktiv, hinweis, busy, onWaehle, onZeitenBearbeiten }: {
  einsatz: Record<string, any>
  aktuell: EinsatzPhaseKey | null
  /** false = ausgegraut (Übung nicht LAUFEND) */
  aktiv: boolean
  hinweis?: string | null
  busy?: boolean
  onWaehle: (ziel: EinsatzPhaseKey) => void
  /** Öffnet den Dialog zum nachträglichen Ändern der Phasenzeiten (nur bei Typ EINSATZ/UEBUNGSEINSATZ) */
  onZeitenBearbeiten?: () => void
}) {
  const phasen = phasenFuerTyp(einsatz.typ)
  const idx = phaseIndex(aktuell, einsatz.typ)
  const mitZeiten = einsatz.typ === 'EINSATZ' || einsatz.typ === 'UEBUNGSEINSATZ'
  return (
    <Card className="p-4" data-testid="phasen-zeitleiste">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-dark-text">Phase</h3>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-primary-400" />}
        {mitZeiten && onZeitenBearbeiten && (
          <button type="button" onClick={onZeitenBearbeiten} data-testid="zeiten-bearbeiten"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-dark-border px-3 text-sm text-dark-text-muted hover:bg-dark-bg-tertiary hover:text-dark-text">
            <Clock className="h-4 w-4" />Zeiten bearbeiten
          </button>
        )}
        {hinweis && <p className="text-sm text-amber-300" role="status">{hinweis}</p>}
      </div>
      <ol className={`grid ${SPALTEN[phasen.length] ?? 'grid-cols-5'} gap-1 sm:gap-2 ${aktiv ? '' : 'opacity-50'}`}>
        {phasen.map((p, i) => {
          const fertig = idx >= 0 && i < idx
          const jetzt = i === idx
          const zeit = mitZeiten ? uhrzeit(einsatz[ZEIT_FELD[p.key] ?? '']) : ''
          return (
            <li key={p.key} className="min-w-0">
              <button
                type="button"
                disabled={!aktiv || busy}
                aria-current={jetzt ? 'step' : undefined}
                data-phase={p.key}
                onClick={() => !jetzt && onWaehle(p.key)}
                className={`flex min-h-[64px] w-full flex-col items-center justify-center gap-1 rounded-lg border px-1 py-2 text-center text-xs transition-colors sm:text-sm disabled:cursor-not-allowed ${
                  jetzt ? 'border-primary-400 bg-primary-600/30 font-semibold text-dark-text'
                    : fertig ? 'border-green-500/40 bg-green-500/10 text-dark-text'
                    : 'border-dark-border bg-dark-bg-tertiary/50 text-dark-text-muted hover:bg-dark-bg-tertiary'}`}
              >
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                  jetzt ? 'bg-primary-500 text-white' : fertig ? 'bg-green-500 text-white' : 'bg-dark-border text-dark-text-muted'}`}>
                  {fertig ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span className="[overflow-wrap:anywhere] hyphens-auto text-[11px] leading-tight sm:text-sm">{p.label}</span>
                {zeit && <span className="text-[11px] text-dark-text-muted">{zeit} Uhr</span>}
              </button>
            </li>
          )
        })}
      </ol>
    </Card>
  )
}
